import { resolverContextoFinanciero, safeMoneyNumber } from "./contextoFinanciero";
import {
  esPropiedadDeSocio,
  generaUtilidadKye,
  getPropiedadIdsDeuda,
} from "./compatFinanciera";
import { normalizeMetodoPago } from "./metodosPago";

const estadoPropiedad = (propiedad = {}) =>
  String(propiedad.estado || "").trim().toLowerCase();

const isVendida = (propiedad = {}) => {
  const estado = estadoPropiedad(propiedad);
  return estado === "vendida" || estado === "vendido";
};

const hasDeudaForPropiedad = (deudas = [], propiedadId = "") =>
  deudas.some((deuda) => {
    if (deuda.propiedadId === propiedadId) return true;
    if (Array.isArray(deuda.propiedadIds)) return deuda.propiedadIds.includes(propiedadId);
    return false;
  });

const makeIssue = ({
  severidad = "advertencia",
  tipo,
  entidadTipo,
  entidadId,
  titulo,
  detalle,
  link = "",
}) => ({
  id: `${tipo}:${entidadTipo}:${entidadId}`,
  severidad,
  tipo,
  entidadTipo,
  entidadId,
  titulo,
  detalle,
  link,
});

const getPrecioPropiedad = (propiedad = {}) =>
  safeMoneyNumber(propiedad.precioVenta ?? propiedad.precioFinanciado ?? propiedad.precioContado);

export const construirValidacionesErp = ({
  propiedades = [],
  deudas = [],
  cxpItems = [],
  movimientosCaja = [],
  lotesFinancieros = {},
  catalogos = {},
} = {}) => {
  const issues = [];
  const propiedadesActivas = propiedades.filter((p) => p.activo !== false);
  const lotesRows = lotesFinancieros.rows || [];
  const obras = Array.from(catalogos.obras?.values?.() || []);
  const fraccionamientos = Array.from(catalogos.fraccionamientos?.values?.() || []);
  const movimientosCajaActivos = movimientosCaja.filter((mov) => mov.activo !== false && mov.cancelado !== true);
  const movimientosCajaPorCxp = movimientosCajaActivos.reduce((acc, mov) => {
    if (!mov.cxpId) return acc;
    const list = acc.get(mov.cxpId) || [];
    list.push(mov);
    acc.set(mov.cxpId, list);
    return acc;
  }, new Map());

  fraccionamientos.forEach((fraccionamiento) => {
    if (!fraccionamiento.proyectoId) {
      issues.push(
        makeIssue({
          severidad: "info",
          tipo: "fraccionamiento_sin_proyecto",
          entidadTipo: "fraccionamiento",
          entidadId: fraccionamiento.id,
          titulo: "Fraccionamiento sin proyecto",
          detalle: `${fraccionamiento.nombre || fraccionamiento.id} no tiene proyecto asignado. Puede seguir operando, pero la lectura por proyecto sera menos clara.`,
          link: "/app/propiedades",
        })
      );
    }
  });

  obras.forEach((obra) => {
    if (!obra.proyectoId && !obra.fraccionamientoId && !obra.propiedadId) {
      issues.push(
        makeIssue({
          severidad: "advertencia",
          tipo: "obra_sin_contexto",
          entidadTipo: "obra",
          entidadId: obra.id,
          titulo: "Obra sin contexto financiero",
          detalle: `${obra.nombre || obra.id} no apunta a proyecto, fraccionamiento ni lote.`,
          link: `/app/obras/${obra.id}`,
        })
      );
    }

    if (safeMoneyNumber(obra.presupuestoTotal) <= 0) {
      issues.push(
        makeIssue({
          severidad: "info",
          tipo: "obra_sin_presupuesto",
          entidadTipo: "obra",
          entidadId: obra.id,
          titulo: "Obra sin presupuesto",
          detalle: `${obra.nombre || obra.id} no tiene presupuesto/costo estimado. Puede operar, pero no mostrara avance de inversion contra objetivo.`,
          link: `/app/obras/${obra.id}`,
        })
      );
    }
  });

  propiedadesActivas.forEach((propiedad) => {
    const vendido = isVendida(propiedad);
    const tieneDeuda = hasDeudaForPropiedad(deudas, propiedad.id);
    const loteFinanciero = lotesRows.find((row) => row.id === propiedad.id);
    const ingreso = loteFinanciero?.ingresoVendido ?? getPrecioPropiedad(propiedad);
    const nombre = propiedad.codigoHumano || propiedad.folio || propiedad.id;
    const propiedadSocio = esPropiedadDeSocio(propiedad);
    const generaUtilidad = generaUtilidadKye(propiedad);

    if (propiedadSocio && !generaUtilidad) {
      issues.push(
        makeIssue({
          severidad: "info",
          tipo: "propiedad_socio_sin_utilidad_kye",
          entidadTipo: "propiedad",
          entidadId: propiedad.id,
          titulo: "Propiedad de socio excluida de utilidad K&E",
          detalle: `${nombre} esta vinculada a socio/tercero y se excluye del inventario vendido y utilidad K&E por compatibilidad de lectura.`,
          link: `/app/propiedades/${propiedad.id}`,
        })
      );
    }

    if (vendido && !tieneDeuda && generaUtilidad) {
      issues.push(
        makeIssue({
          severidad: "critica",
          tipo: "lote_vendido_sin_deuda",
          entidadTipo: "propiedad",
          entidadId: propiedad.id,
          titulo: "Lote vendido sin deuda asociada",
          detalle: `${nombre} esta marcado como vendido, pero no tiene deuda/venta relacionada.`,
          link: `/app/propiedades/${propiedad.id}`,
        })
      );
    }

    if (vendido && safeMoneyNumber(ingreso) <= 0 && generaUtilidad) {
      issues.push(
        makeIssue({
          severidad: "critica",
          tipo: "lote_vendido_sin_precio",
          entidadTipo: "propiedad",
          entidadId: propiedad.id,
          titulo: "Lote vendido sin precio detectable",
          detalle: `${nombre} no tiene ingreso/precio suficiente para calcular utilidad.`,
          link: `/app/propiedades/${propiedad.id}`,
        })
      );
    }

    if (!propiedad.fraccionamientoId && !propiedad.fraccNombre) {
      issues.push(
        makeIssue({
          severidad: "advertencia",
          tipo: "propiedad_sin_fraccionamiento",
          entidadTipo: "propiedad",
          entidadId: propiedad.id,
          titulo: "Propiedad sin fraccionamiento",
          detalle: `${nombre} no tiene fraccionamiento asignado. Puede quedar fuera de agrupaciones.`,
          link: `/app/propiedades/${propiedad.id}`,
        })
      );
    }

    if (!propiedad.proyectoId && !propiedad.fraccionamientoId && !propiedad.fraccNombre) {
      issues.push(
        makeIssue({
          severidad: "info",
          tipo: "propiedad_sin_proyecto",
          entidadTipo: "propiedad",
          entidadId: propiedad.id,
          titulo: "Propiedad sin proyecto",
          detalle: `${nombre} no tiene proyecto ni fraccionamiento; se agrupara como "Sin proyecto".`,
          link: `/app/propiedades/${propiedad.id}`,
        })
      );
    }
  });

  deudas.forEach((deuda) => {
    const propiedadIds = getPropiedadIdsDeuda(deuda);
    if (propiedadIds.length <= 1) return;

    issues.push(
      makeIssue({
        severidad: "info",
        tipo: "deuda_multipropiedad_distribuida",
        entidadTipo: "deuda",
        entidadId: deuda.id,
        titulo: "Contrato multipropiedad distribuido",
        detalle: `La deuda ${deuda.folio || deuda.id} vincula ${propiedadIds.length} propiedades; ERP distribuye monto, pagos y saldo entre los lotes para no inflar utilidad esperada.`,
        link: deuda.clienteId
          ? `/app/cliente/${deuda.clienteId}/deudas/${deuda.id}`
          : "",
      })
    );
  });

  cxpItems.forEach((gasto) => {
    const importe = safeMoneyNumber(gasto.importe);
    const proveedor = gasto.proveedorNombre || gasto.proveedor || "";
    const contexto = resolverContextoFinanciero(gasto, catalogos);
    const capitalizable = gasto.capitalizable === true;
    const link = `/app/cxp/deudas/${gasto.id}`;
    const obra = gasto.obraId ? catalogos.obras?.get?.(gasto.obraId) : null;

    if (!proveedor) {
      issues.push(
        makeIssue({
          severidad: "advertencia",
          tipo: "cxp_sin_proveedor",
          entidadTipo: "cxp",
          entidadId: gasto.id,
          titulo: "CxP sin proveedor",
          detalle: `La CxP ${gasto.referencia || gasto.id} no tiene proveedor definido.`,
          link,
        })
      );
    }

    if (!gasto.proveedorId) {
      issues.push(
        makeIssue({
          severidad: "advertencia",
          tipo: "cxp_sin_proveedor_id",
          entidadTipo: "cxp",
          entidadId: gasto.id,
          titulo: "CxP sin proveedor vinculado",
          detalle: `La CxP ${gasto.referencia || gasto.id} tiene proveedor de texto, pero no proveedorId para reportes y trazabilidad.`,
          link,
        })
      );
    }

    if (importe <= 0) {
      issues.push(
        makeIssue({
          severidad: "critica",
          tipo: "cxp_sin_importe",
          entidadTipo: "cxp",
          entidadId: gasto.id,
          titulo: "CxP sin importe valido",
          detalle: `La CxP ${gasto.referencia || gasto.id} no tiene importe positivo.`,
          link,
        })
      );
    }

    if (capitalizable && contexto.nivel === "administrativo") {
      issues.push(
        makeIssue({
          severidad: "critica",
          tipo: "capitalizable_sin_contexto",
          entidadTipo: "cxp",
          entidadId: gasto.id,
          titulo: "Gasto capitalizable sin contexto",
          detalle: `La CxP ${gasto.referencia || gasto.id} es capitalizable, pero no apunta a proyecto, fraccionamiento, obra o lote.`,
          link,
        })
      );
    }

    if (
      capitalizable &&
      contexto.nivel !== "administrativo" &&
      !gasto.propiedadId &&
      (gasto.metodoProrrateo || "ninguno") === "ninguno"
    ) {
      issues.push(
        makeIssue({
          severidad: "advertencia",
          tipo: "capitalizable_sin_prorrateo",
          entidadTipo: "cxp",
          entidadId: gasto.id,
          titulo: "Gasto capitalizable sin prorrateo",
          detalle: `La CxP ${gasto.referencia || gasto.id} tiene contexto financiero, pero metodo de prorrateo en ninguno.`,
          link,
        })
      );
    }

    if (
      obra &&
      ((gasto.proyectoId && obra.proyectoId && gasto.proyectoId !== obra.proyectoId) ||
        (gasto.fraccionamientoId && obra.fraccionamientoId && gasto.fraccionamientoId !== obra.fraccionamientoId) ||
        (gasto.propiedadId && obra.propiedadId && gasto.propiedadId !== obra.propiedadId))
    ) {
      issues.push(
        makeIssue({
          severidad: "advertencia",
          tipo: "cxp_contexto_contradictorio_obra",
          entidadTipo: "cxp",
          entidadId: gasto.id,
          titulo: "CxP con contexto distinto al de la obra",
          detalle: `La CxP ${gasto.referencia || gasto.id} esta ligada a una obra, pero tiene proyecto/fraccionamiento/lote distinto al heredado.`,
          link,
        })
      );
    }

    const pagos = Array.isArray(gasto.pagos) ? gasto.pagos : [];
    const pagada = gasto.activo === false && gasto.estado !== "cancelada" && safeMoneyNumber(gasto.saldoRestante) <= 0;
    const saldoCxp = safeMoneyNumber(gasto.saldoRestante);
    const cancelada = gasto.cancelada === true || String(gasto.estado || "").toLowerCase() === "cancelada";
    const movimientosCajaCxp = movimientosCajaPorCxp.get(gasto.id) || [];

    if (gasto.activo === true && saldoCxp <= 0) {
      issues.push(
        makeIssue({
          severidad: "advertencia",
          tipo: "cxp_activa_saldo_cero",
          entidadTipo: "cxp",
          entidadId: gasto.id,
          titulo: "CxP activa con saldo cero",
          detalle: `La CxP ${gasto.referencia || gasto.id} esta activa, pero no tiene saldo pendiente.`,
          link,
        })
      );
    }

    if (cancelada && movimientosCajaCxp.length > 0) {
      issues.push(
        makeIssue({
          severidad: "advertencia",
          tipo: "cxp_cancelada_con_caja_activa",
          entidadTipo: "cxp",
          entidadId: gasto.id,
          titulo: "CxP cancelada con caja vinculada",
          detalle: `La CxP ${gasto.referencia || gasto.id} esta cancelada y conserva ${movimientosCajaCxp.length} movimiento(s) de caja activo(s). Revisa si es historico esperado o requiere reversa.`,
          link,
        })
      );
    }

    if (pagada && pagos.length > 0 && movimientosCajaCxp.length === 0) {
      issues.push(
        makeIssue({
          severidad: "advertencia",
          tipo: "cxp_pagada_sin_movimiento_caja",
          entidadTipo: "cxp",
          entidadId: gasto.id,
          titulo: "CxP pagada sin movimiento de caja",
          detalle: `La CxP ${gasto.referencia || gasto.id} tiene pagos o saldo liquidado, pero no tiene salida de caja vinculada.`,
          link,
        })
      );
    }

    if (capitalizable && pagada && contexto.nivel === "administrativo") {
      issues.push(
        makeIssue({
          severidad: "critica",
          tipo: "capitalizable_pagada_sin_contexto",
          entidadTipo: "cxp",
          entidadId: gasto.id,
          titulo: "Gasto capitalizable pagado sin contexto",
          detalle: `La CxP ${gasto.referencia || gasto.id} ya tiene pago, es capitalizable y aun no apunta a proyecto, fraccionamiento, obra o lote.`,
          link,
        })
      );
    }
  });

  movimientosCajaActivos.forEach((mov) => {
    const metodo = normalizeMetodoPago(mov.metodo || mov.metodoEtiqueta || "");
    const esTransferenciaBancaria =
      mov.esTransferenciaBancaria === true || metodo.transferencia;

    if (!mov.proyectoId) {
      issues.push(
        makeIssue({
          severidad: "advertencia",
          tipo: "movimiento_caja_sin_proyecto",
          entidadTipo: "caja",
          entidadId: mov.id,
          titulo: "Movimiento de caja sin proyecto",
          detalle: `El movimiento ${mov.concepto || mov.id} no tiene proyecto y no puede agruparse en caja por proyecto.`,
          link: "",
        })
      );
    }

    if (
      esTransferenciaBancaria &&
      !String(mov.referencia || "").trim() &&
      mov.transferenciaInterna !== true
    ) {
      issues.push(
        makeIssue({
          severidad: "advertencia",
          tipo: "transferencia_sin_referencia",
          entidadTipo: "caja",
          entidadId: mov.id,
          titulo: "Transferencia sin referencia",
          detalle: `El movimiento ${mov.concepto || mov.id} esta marcado como ${mov.metodoEtiqueta || metodo.label}, pero no tiene referencia bancaria/folio.`,
          link: mov.proyectoId ? `/app/proyectos/${mov.proyectoId}/caja` : "/app/cajas/general",
        })
      );
    }

    if (mov.cxpId && !mov.cxpPagoId) {
      issues.push(
        makeIssue({
          severidad: "advertencia",
          tipo: "movimiento_caja_cxp_sin_pago_id",
          entidadTipo: "caja",
          entidadId: mov.id,
          titulo: "Movimiento CxP sin pago vinculado",
          detalle: `El movimiento ${mov.concepto || mov.id} apunta a una CxP, pero no tiene cxpPagoId para conciliacion exacta.`,
          link: mov.proyectoId ? `/app/proyectos/${mov.proyectoId}/caja` : "/app/cajas/general",
        })
      );
    }
  });

  movimientosCajaPorCxp.forEach((movimientos, cxpId) => {
    const activos = movimientos.filter((mov) => mov.tipo === "salida");
    if (activos.length <= 1) return;
    const cxp = cxpItems.find((item) => item.id === cxpId);
    issues.push(
      makeIssue({
        severidad: "advertencia",
        tipo: "movimiento_caja_duplicado_cxp",
        entidadTipo: "caja",
        entidadId: cxpId,
        titulo: "Movimiento de caja duplicado para CxP",
        detalle: `La CxP ${cxp?.referencia || cxpId} tiene ${activos.length} salidas de caja activas vinculadas. Verifica que no exista doble pago.`,
        link: `/app/cxp/deudas/${cxpId}`,
      })
    );
  });

  const resumen = issues.reduce(
    (acc, issue) => {
      acc.total += 1;
      acc[issue.severidad] = (acc[issue.severidad] || 0) + 1;
      return acc;
    },
    { total: 0, critica: 0, advertencia: 0, info: 0 }
  );

  const score = Math.max(
    0,
    100 - resumen.critica * 12 - resumen.advertencia * 4
  );

  const orderedIssues = issues.sort((a, b) => {
    const weight = { critica: 0, advertencia: 1, info: 2 };
    return (weight[a.severidad] ?? 9) - (weight[b.severidad] ?? 9);
  });

  return {
    score,
    resumen,
    rows: orderedIssues.slice(0, 250),
    truncated: orderedIssues.length > 250,
    totalRowsOriginal: orderedIssues.length,
  };
};
